/**
 * Auto-Discovery Panel Component
 * 
 * Shows auto-discovered settlement matches from the Transaction-First system.
 * Allows users to:
 * 1. Review auto-discovered matches by confidence level
 * 2. Approve or undo auto-discoveries
 * 3. Manually match via Special Item Schedule
 * 4. Bulk auto-approve very high confidence items
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { getCurrencySymbol } from '../../utils/currency';
import {
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  Undo2,
  Search,
  Target,
  Zap,
  Building2,
  Users,
  Banknote,
  TrendingUp,
  Landmark,
  Calendar,
  ChevronRight,
  X,
  ThumbsUp,
  Eye
} from 'lucide-react';

const AutoDiscoveryPanel = ({ business, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [specialItemSchedule, setSpecialItemSchedule] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [undoReason, setUndoReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [autoApproving, setAutoApproving] = useState(false);

  const currencySymbol = getCurrencySymbol(business?.currency || 'NGN');
  const formatCurrency = (amount) => `${currencySymbol}${Number(amount || 0).toLocaleString()}`;

  useEffect(() => {
    fetchAutoDiscoveries();
  }, []);

  const fetchAutoDiscoveries = async () => {
    setLoading(true);
    try {
      const [pendingRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/auto-discovery/pending`),
        axios.get(`${API}/auto-discovery/special-item-schedule`)
      ]);

      setItems(pendingRes.data.items || []);
      setSummary(pendingRes.data.summary || {});
      setSpecialItemSchedule(scheduleRes.data);
    } catch (err) {
      console.error('Failed to fetch auto-discoveries:', err);
      toast.error('Failed to load auto-discovered items');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (item) => {
    setProcessing(true);
    try {
      await axios.post(
        `${API}/auto-discovery/approve/${item.document_type}/${item.id}`);
      toast.success('Auto-discovered match approved');
      fetchAutoDiscoveries();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to approve:', err);
      toast.error('Failed to approve match');
    } finally {
      setProcessing(false);
    }
  };

  const handleUndo = async () => {
    if (!selectedItem) return;
    setProcessing(true);
    try {
      await axios.post(
        `${API}/auto-discovery/undo/${selectedItem.document_type}/${selectedItem.id}`,
        { reason: undoReason });
      toast.success('Auto-discovery undone - Transaction marked for review');
      setShowUndoDialog(false);
      setSelectedItem(null);
      setUndoReason('');
      fetchAutoDiscoveries();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to undo:', err);
      toast.error(err.response?.data?.detail || 'Failed to undo classification');
    } finally {
      setProcessing(false);
    }
  };

  const handleAutoApproveAll = async () => {
    setAutoApproving(true);
    try {
      const res = await axios.post(
        `${API}/auto-discovery/auto-approve-high-confidence`);
      toast.success(`Auto-approved ${res.data.approved_count} high-confidence matches`);
      fetchAutoDiscoveries();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to auto-approve:', err);
      toast.error('Failed to auto-approve');
    } finally {
      setAutoApproving(false);
    }
  };

  const getConfidenceColor = (level) => {
    switch (level) {
      case 'VERY_HIGH': return 'bg-green-100 text-green-700 border-green-300';
      case 'HIGH': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'MEDIUM': return 'bg-amber-100 text-amber-700 border-amber-300';
      default: return 'bg-red-100 text-red-700 border-red-300';
    }
  };

  const getDiscoveryIcon = (type) => {
    if (type?.includes('payroll') || type?.includes('salary')) return Users;
    if (type?.includes('paye') || type?.includes('tax') || type?.includes('vat') || type?.includes('wht')) return Building2;
    if (type?.includes('pension') || type?.includes('nhf')) return Landmark;
    if (type?.includes('loan')) return Banknote;
    if (type?.includes('investment')) return TrendingUp;
    return Clock;
  };

  const AutoDiscoveryItem = ({ item }) => {
    const Icon = getDiscoveryIcon(item.discovery_source || item.payment_type);
    
    return (
      <div 
        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
        data-testid={`auto-discovery-item-${item.id}`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            item.direction === 'inflow' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
          }`}>
            {item.direction === 'inflow' ? <ArrowDownLeft className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
          </div>
          <div>
            <p className="font-medium">{item.description}</p>
            <p className="text-sm text-muted-foreground">
              {item.counterparty_name} • {item.date}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className={getConfidenceColor(item.confidence_level)}>
                <Icon className="w-3 h-3 mr-1" />
                {item.confidence_level}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {item.discovery_source?.replace(/_/g, ' ')}
              </Badge>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-lg font-bold">{formatCurrency(item.amount)}</p>
            <p className="text-xs text-muted-foreground">
              Score: {item.discovery_confidence}
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedItem(item);
                setShowUndoDialog(true);
              }}
              data-testid={`undo-btn-${item.id}`}
            >
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              onClick={() => handleApprove(item)}
              disabled={processing}
              data-testid={`approve-btn-${item.id}`}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Approve
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const SpecialItemRow = ({ item, category }) => {
    const categoryIcons = {
      payroll: Users,
      loans: Banknote,
      investments: TrendingUp,
      tax: Building2
    };
    const Icon = categoryIcons[category] || Clock;

    return (
      <div 
        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors text-sm"
        data-testid={`special-item-${item.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium">{item.display_name}</p>
            <p className="text-xs text-muted-foreground">
              {item.period || item.due_date || item.expected_date}
              {item.lender && ` • ${item.lender}`}
              {item.institution && ` • ${item.institution}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="font-semibold">{formatCurrency(item.amount)}</p>
          <Badge variant="outline" className={item.can_match ? 'text-amber-600 border-amber-300' : 'text-green-600 border-green-300'}>
            {item.status}
          </Badge>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="auto-discovery-panel">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-700">Very High</span>
            </div>
            <p className="text-2xl font-bold text-green-800 mt-1">{summary.very_high_confidence || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <ThumbsUp className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">High</span>
            </div>
            <p className="text-2xl font-bold text-blue-800 mt-1">{summary.high_confidence || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-700">Medium</span>
            </div>
            <p className="text-2xl font-bold text-amber-800 mt-1">{summary.medium_confidence || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-xs font-medium text-red-700">Low</span>
            </div>
            <p className="text-2xl font-bold text-red-800 mt-1">{summary.low_confidence || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Auto-Approve Button */}
      {summary.very_high_confidence > 0 && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="pt-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-green-800">
                {summary.very_high_confidence} Very High Confidence Matches Ready
              </p>
              <p className="text-sm text-green-700">
                Auto-approve all matches with 90%+ confidence score
              </p>
            </div>
            <Button 
              onClick={handleAutoApproveAll}
              disabled={autoApproving}
              className="bg-green-600 hover:bg-green-700"
              data-testid="auto-approve-all-btn"
            >
              {autoApproving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Auto-Approve All
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Auto-Discovered ({items.length})
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            Special Item Schedule
          </TabsTrigger>
        </TabsList>

        {/* Pending Auto-Discoveries */}
        <TabsContent value="pending" className="space-y-4">
          {items.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold">No Pending Auto-Discoveries</h3>
                <p className="text-muted-foreground">
                  Transaction-first auto-discovery will create items when bank transactions arrive
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <AutoDiscoveryItem key={item.id} item={item} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Special Item Schedule */}
        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-500" />
                Special Item Schedule
              </CardTitle>
              <CardDescription>
                Pending items available for manual matching to bank transactions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Payroll */}
              {specialItemSchedule?.schedule?.payroll?.length > 0 && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-blue-500" />
                    Payroll ({specialItemSchedule.totals?.payroll?.count || 0})
                    <Badge variant="outline">{formatCurrency(specialItemSchedule.totals?.payroll?.amount || 0)}</Badge>
                  </h4>
                  <div className="space-y-2">
                    {specialItemSchedule.schedule.payroll.slice(0, 5).map((item) => (
                      <SpecialItemRow key={item.id} item={item} category="payroll" />
                    ))}
                  </div>
                </div>
              )}

              {/* Loans */}
              {specialItemSchedule?.schedule?.loans?.length > 0 && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <Banknote className="w-4 h-4 text-rose-500" />
                    Loans ({specialItemSchedule.totals?.loans?.count || 0})
                    <Badge variant="outline">{formatCurrency(specialItemSchedule.totals?.loans?.amount || 0)}</Badge>
                  </h4>
                  <div className="space-y-2">
                    {specialItemSchedule.schedule.loans.slice(0, 5).map((item) => (
                      <SpecialItemRow key={item.id} item={item} category="loans" />
                    ))}
                  </div>
                </div>
              )}

              {/* Investments */}
              {specialItemSchedule?.schedule?.investments?.length > 0 && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                    Investments ({specialItemSchedule.totals?.investments?.count || 0})
                    <Badge variant="outline">{formatCurrency(specialItemSchedule.totals?.investments?.amount || 0)}</Badge>
                  </h4>
                  <div className="space-y-2">
                    {specialItemSchedule.schedule.investments.slice(0, 5).map((item) => (
                      <SpecialItemRow key={item.id} item={item} category="investments" />
                    ))}
                  </div>
                </div>
              )}

              {/* Tax */}
              {specialItemSchedule?.schedule?.tax?.length > 0 && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-purple-500" />
                    Tax ({specialItemSchedule.totals?.tax?.count || 0})
                    <Badge variant="outline">{formatCurrency(specialItemSchedule.totals?.tax?.amount || 0)}</Badge>
                  </h4>
                  <div className="space-y-2">
                    {specialItemSchedule.schedule.tax.slice(0, 5).map((item) => (
                      <SpecialItemRow key={item.id} item={item} category="tax" />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {(!specialItemSchedule?.schedule?.payroll?.length && 
                !specialItemSchedule?.schedule?.loans?.length && 
                !specialItemSchedule?.schedule?.investments?.length &&
                !specialItemSchedule?.schedule?.tax?.length) && (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <p className="text-muted-foreground">No pending items in schedule</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="border-dashed bg-muted/30">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-2">Transaction-First Auto-Discovery:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Very High Confidence (90+):</strong> Can be auto-approved in batch</li>
                <li><strong>High Confidence (70-89):</strong> Recommended for approval</li>
                <li><strong>Medium Confidence (50-69):</strong> Review before approving</li>
                <li><strong>Undo:</strong> Reverts to manual review - use Special Item Schedule to manually match</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Undo Dialog */}
      <Dialog open={showUndoDialog} onOpenChange={setShowUndoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Undo Auto-Discovery</DialogTitle>
            <DialogDescription>
              This will remove the auto-discovered document and mark the bank transaction for manual review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedItem && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedItem.description}</p>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(selectedItem.amount)} • {selectedItem.counterparty_name}
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Textarea
                value={undoReason}
                onChange={(e) => setUndoReason(e.target.value)}
                placeholder="Why are you undoing this classification?"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUndoDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleUndo}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
              Undo Classification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { AutoDiscoveryPanel };
export default AutoDiscoveryPanel;
